import { vars } from "@vericus/cadmus-ui";

import {
  CriterionFragment,
  CriterionOutcomeFragment,
  ScaleItemFragment,
} from "@/generated/graphql";

export const CRITERIA_COLUMN_LABEL = "Criteria";
export const OVERALL_COLUMN_LABEL = "Overall";
export const SCORES_COLUMN_LABEL = "Your Scores";
export const SELF_COLUMN_LABEL = "Self";

export type PeerEvaluationRow = {
  criterionId: string;
  criterionTitle: string;
  criterionColor: string;
  averageScore: number;
  maxScore: number; // Fixed: should match the return key
  matchingScaleItem: ScaleItemFragment | null;
};

export const processPeerEvaluationRows = (
  outcomes: CriterionOutcomeFragment[],
  criteria: readonly CriterionFragment[]
): PeerEvaluationRow[] => {
  const validCriteria: (CriterionFragment & { id: string })[] = criteria.filter(
    (criterion): criterion is CriterionFragment & { id: string } =>
      !!criterion.id
  );

  // Build map: criterionId -> CriterionFragment
  const criterionById = new Map<string, CriterionFragment>();
  validCriteria.forEach((criterion) => {
    if (criterion.id) {
      criterionById.set(criterion.id, criterion);
    }
  });

  const scoresByCriterionId = new Map<string, number[]>();

  outcomes.forEach((peerOutcome) => {
    const criterionId = peerOutcome.markerRubricCriterionId;
    if (!criterionId || !criterionById.has(criterionId)) return;

    if (!scoresByCriterionId.has(criterionId)) {
      scoresByCriterionId.set(criterionId, []);
    }
    scoresByCriterionId.get(criterionId)!.push(peerOutcome.markerScore);
  });

  const peerEvaluationRows = validCriteria.map((criterion) => {
    const scores = scoresByCriterionId.get(criterion.id) ?? [];
    const averageScore =
      scores.length > 0
        ? Math.floor(
            scores.reduce((sum, score) => sum + score, 0) / scores.length
          )
        : 0;

    const matchingScaleItem =
      criterion.scaleItems
        .toSorted((a, b) => (a.rangeTo ?? 0) - (b.rangeTo ?? 0))
        .find((scale, index) => {
          // Handle absolute format (rangeFrom is null)
          if (scale.rangeFrom === null) {
            const prevRangeTo =
              index > 0
                ? (criterion.scaleItems[index - 1]?.rangeTo ??
                  Number.NEGATIVE_INFINITY)
                : Number.NEGATIVE_INFINITY;
            const currentRangeTo = scale.rangeTo ?? Number.POSITIVE_INFINITY;
            return averageScore > prevRangeTo && averageScore <= currentRangeTo;
          }
          // Handle range formats (percentage/points)
          return (
            averageScore >= (scale.rangeFrom ?? 0) &&
            averageScore <= (scale.rangeTo ?? 0)
          );
        }) ?? null;

    return {
      criterionId: criterion.id,
      criterionTitle: criterion.skill?.skill ?? criterion.title ?? "",
      criterionColor: criterion.color ?? "",
      averageScore: Number(averageScore.toFixed(2)),
      maxScore: Math.max(...criterion.scaleItems.map((s) => s.rangeTo ?? 0)),
      matchingScaleItem,
    };
  });

  return peerEvaluationRows;
};

export const transformPeerOutcomes = (
  selfId: string | undefined,
  receivedCriterionOutcomes: CriterionOutcomeFragment[] | undefined,
  peerMarkerRubric: { criteria: readonly CriterionFragment[] }
): {
  criteria: readonly CriterionFragment[];
  peerRows: PeerEvaluationRow[];
  selfRows: PeerEvaluationRow[];
} => {
  const { criteria } = peerMarkerRubric;

  const selfCriterions: CriterionOutcomeFragment[] = [];
  const peerOnlyCriterions: CriterionOutcomeFragment[] = [];

  if (receivedCriterionOutcomes) {
    for (const outcome of receivedCriterionOutcomes) {
      if (outcome.markerUserId === selfId) {
        selfCriterions.push(outcome);
      } else {
        peerOnlyCriterions.push(outcome);
      }
    }
  }

  const peerRows = processPeerEvaluationRows(peerOnlyCriterions, criteria);
  const selfRows = selfCriterions
    ? processPeerEvaluationRows(
        selfCriterions as CriterionOutcomeFragment[],
        criteria
      )
    : [];

  return {
    criteria,
    peerRows,
    selfRows,
  };
};

/**
 * Format a floating point number to two decimal places without a trailing zero.
 *
 * @example
 *
 *     formatDecimalNumber(2) // "2"
 *     formatDecimalNumber(2.231231111) // "2.31"
 *     formatDecimalNumber(2.200000000) // "2.2"
 *     formatDecimalNumber(2.238) // "2.24"
 *
 */
export function formatDecimalNumber(num: number): string {
  return num.toFixed(2).replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/, "$1");
}

export const getScoreColor = (
  averageScore?: number | null,
  maxScore?: number | null
) => {
  const ratioMaxScore = maxScore ?? 0;
  if (ratioMaxScore === 0) return vars.colors.sgrey50;

  const ratio = (averageScore ?? 0) / ratioMaxScore;

  if (ratio <= 0.2) return "#F6E4ED";
  if (ratio <= 0.4) return "#FDEBE2";
  if (ratio <= 0.6) return "#FDF8E2";
  if (ratio <= 0.8) return "#DEEAF7";
  return "#E1F4EE";
};

/**
 * Checks if a scale item is selected based on the current score.
 *
 * If the scale item has a null `rangeFrom`, it is treated as an upper-bound-only item.
 * We imply a lower bound based on the previous item's `rangeTo` value.
 *
 */
export function isScaleItemSelected(
  scaleItems: ScaleItemFragment[],
  scaleItem: ScaleItemFragment,
  currentScore?: number | null
) {
  if (currentScore === undefined || currentScore === null) return false;

  if (scaleItem.rangeFrom === null) {
    const sortedScaleItems = scaleItems.toSorted(
      (a, b) => (a.rangeTo ?? 0) - (b.rangeTo ?? 0)
    );
    const itemIndex = sortedScaleItems.findIndex(
      (item) => item.id === scaleItem.id
    );

    const prevRangeTo =
      itemIndex > 0
        ? (sortedScaleItems[itemIndex - 1]?.rangeTo ?? Number.NEGATIVE_INFINITY)
        : Number.NEGATIVE_INFINITY;
    const upper = scaleItem.rangeTo ?? Number.POSITIVE_INFINITY;

    return currentScore > prevRangeTo && currentScore <= upper;
  }

  // For range formats, check if score is within the range
  return (
    scaleItem.rangeFrom != null &&
    scaleItem.rangeTo != null &&
    currentScore >= scaleItem.rangeFrom &&
    currentScore <= scaleItem.rangeTo
  );
}
